import invariant from "tiny-invariant"

import Big from "./Big"
import type { BigintIsh } from "./BigintIsh"
import Decimal from "./Decimal"
import Rounding, { toFixedRounding, toSignificantRounding } from "./Rounding"

/**
 * The `Fraction` class represents a fractional value with a numerator and a denominator.
 * It provides various mathematical operations and formatting options for fractional values.
 */
class Fraction {
  /**
   * The numerator of the fraction.
   * @type {bigint}
   */
  readonly numerator: bigint

  /**
   * The denominator of the fraction.
   * @type {bigint}
   */
  readonly denominator: bigint

  /**
   * Constructs a `Fraction` instance with a numerator and a denominator.
   * @param {BigintIsh} numerator - The numerator of the fraction.
   * @param {BigintIsh} denominator - The denominator of the fraction (default is 1).
   */
  constructor(numerator: BigintIsh, denominator: BigintIsh = 1n) {
    this.numerator =
      typeof numerator !== "bigint" ? BigInt(numerator) : numerator
    this.denominator =
      typeof denominator !== "bigint" ? BigInt(denominator) : denominator
  }

  /**
   * Tries to parse a `Fraction` from a given input (either a number, string, bigint, or existing `Fraction`).
   * @param {BigintIsh | Fraction} fractionish - The input value to parse into a `Fraction`.
   * @returns {Fraction} - A `Fraction` instance representing the parsed value.
   * @throws {Error} - Throws an error if the input cannot be parsed into a `Fraction`.
   */
  private static tryParseFraction(fractionish: BigintIsh | Fraction): Fraction {
    if (
      typeof fractionish === "number" ||
      typeof fractionish === "string" ||
      typeof fractionish === "bigint"
    )
      return new Fraction(fractionish)

    if ("numerator" in fractionish && "denominator" in fractionish)
      return fractionish
    throw new Error("Could not parse fraction")
  }

  /**
   * Gets the quotient of the fraction (floor division).
   * @returns {bigint} - The quotient of the fraction.
   */
  get quotient(): bigint {
    return this.numerator / this.denominator
  }

  /**
   * Gets the remainder of the fraction after floor division.
   * @returns {Fraction} - A new `Fraction` representing the remainder.
   */
  get remainder(): Fraction {
    return new Fraction(this.numerator % this.denominator, this.denominator)
  }

  /**
   * Inverts the fraction by swapping the numerator and denominator.
   * @returns {Fraction} - A new `Fraction` representing the inverted value.
   */
  invert(): Fraction {
    return new Fraction(this.denominator, this.numerator)
  }

  /**
   * Adds another `Fraction` or a `BigintIsh` value to this `Fraction`.
   * @param {Fraction | BigintIsh} other - The value to add.
   * @returns {Fraction} - A new `Fraction` representing the result of the addition.
   */
  add(other: Fraction | BigintIsh): Fraction {
    const otherParsed = Fraction.tryParseFraction(other)
    if (this.denominator === otherParsed.denominator) {
      return new Fraction(
        this.numerator + otherParsed.numerator,
        this.denominator,
      )
    }
    return new Fraction(
      this.numerator * otherParsed.denominator +
        otherParsed.numerator * this.denominator,
      this.denominator * otherParsed.denominator,
    )
  }

  /**
   * Subtracts another `Fraction` or a `BigintIsh` value from this `Fraction`.
   * @param {Fraction | BigintIsh} other - The value to subtract.
   * @returns {Fraction} - A new `Fraction` representing the result of the subtraction.
   */
  subtract(other: Fraction | BigintIsh): Fraction {
    const otherParsed = Fraction.tryParseFraction(other)
    if (this.denominator === otherParsed.denominator) {
      return new Fraction(
        this.numerator - otherParsed.numerator,
        this.denominator,
      )
    }
    return new Fraction(
      this.numerator * otherParsed.denominator -
        otherParsed.numerator * this.denominator,
      this.denominator * otherParsed.denominator,
    )
  }

  /**
   * Checks if this `Fraction` is less than another `Fraction` or a `BigintIsh` value.
   * @param {Fraction | BigintIsh} other - The value to compare with.
   * @returns {boolean} - True if this `Fraction` is less than the other value, false otherwise.
   */
  lessThan(other: Fraction | BigintIsh): boolean {
    const otherParsed = Fraction.tryParseFraction(other)
    return (
      this.numerator * otherParsed.denominator <
      otherParsed.numerator * this.denominator
    )
  }

  /**
   * Checks if this `Fraction` is equal to another `Fraction` or a `BigintIsh` value.
   * @param {Fraction | BigintIsh} other - The value to compare with.
   * @returns {boolean} - True if this `Fraction` is equal to the other value, false otherwise.
   */
  equalTo(other: Fraction | BigintIsh): boolean {
    const otherParsed = Fraction.tryParseFraction(other)
    return (
      this.numerator * otherParsed.denominator ===
      otherParsed.numerator * this.denominator
    )
  }

  /**
   * Checks if this `Fraction` is greater than another `Fraction` or a `BigintIsh` value.
   * @param {Fraction | BigintIsh} other - The value to compare with.
   * @returns {boolean} - True if this `Fraction` is greater than the other value, false otherwise.
   */
  greaterThan(other: Fraction | BigintIsh): boolean {
    const otherParsed = Fraction.tryParseFraction(other)
    return (
      this.numerator * otherParsed.denominator >
      otherParsed.numerator * this.denominator
    )
  }

  /**
   * Multiplies this `Fraction` by another `Fraction` or a `BigintIsh` value.
   * @param {Fraction | BigintIsh} other - The value to multiply by.
   * @returns {Fraction} - A new `Fraction` representing the result of the multiplication.
   */
  multiply(other: Fraction | BigintIsh): Fraction {
    const otherParsed = Fraction.tryParseFraction(other)
    return new Fraction(
      this.numerator * otherParsed.numerator,
      this.denominator * otherParsed.denominator,
    )
  }

  /**
   * Divides this `Fraction` by another `Fraction` or a `BigintIsh` value.
   * @param {Fraction | BigintIsh} other - The value to divide by.
   * @returns {Fraction} - A new `Fraction` representing the result of the division.
   */
  divide(other: Fraction | BigintIsh): Fraction {
    const otherParsed = Fraction.tryParseFraction(other)
    return new Fraction(
      this.numerator * otherParsed.denominator,
      this.denominator * otherParsed.numerator,
    )
  }

  /**
   * Converts the `Fraction` into a string representation with a specified number of significant digits.
   * @param {number} significantDigits - The number of significant digits to display.
   * @param {object} format - Optional formatting options for the string representation.
   * @param {Rounding} rounding - The rounding mode to use.
   * @returns {string} - A string representation of the `Fraction`.
   * @throws {Error} - Throws an error if the significantDigits parameter is not valid.
   */
  toSignificant(
    significantDigits: number,
    format: object = { groupSeparator: "" },
    rounding: Rounding = Rounding.ROUND_HALF_UP,
  ): string {
    invariant(
      Number.isInteger(significantDigits),
      `${significantDigits} is not an integer.`,
    )
    invariant(significantDigits > 0, `${significantDigits} is not positive.`)

    Decimal.set({
      precision: significantDigits + 1,
      rounding: toSignificantRounding[rounding],
    })
    const quotient = new Decimal(this.numerator.toString())
      .div(this.denominator.toString())
      .toSignificantDigits(significantDigits)
    return quotient.toFormat(quotient.decimalPlaces(), format)
  }

  /**
   * Converts the `Fraction` into a string representation with a fixed number of decimal places.
   * @param {number} decimalPlaces - The number of decimal places to display.
   * @param {object} format - Optional formatting options for the string representation.
   * @param {Rounding} rounding - The rounding mode to use.
   * @returns {string} - A string representation of the `Fraction`.
   * @throws {Error} - Throws an error if the decimalPlaces parameter is not valid.
   */
  toFixed(
    decimalPlaces: number,
    format: object = { groupSeparator: "" },
    rounding: Rounding = Rounding.ROUND_HALF_UP,
  ): string {
    invariant(
      Number.isInteger(decimalPlaces),
      `${decimalPlaces} is not an integer.`,
    )
    invariant(decimalPlaces >= 0, `${decimalPlaces} is negative.`)

    Big.DP = decimalPlaces
    Big.RM = toFixedRounding[rounding]
    return new Big(this.numerator.toString())
      .div(this.denominator.toString())
      .toFormat(decimalPlaces, format)
  }

  /**
   * Converts the `Fraction` into a string representation with number format compact notation over 1 million.
   * @param {number} decimalPlaces - The number of decimal places to display.
   * @param {object} format - Optional formatting options for the string representation.
   * @param {Rounding} rounding - The rounding mode to use.
   * @returns {string} - A string representation of the `Fraction`.
   * @throws {Error} - Throws an error if the decimalPlaces parameter is not valid.
   */
  toNotation(
    decimalPlaces: number,
    format: object = { groupSeparator: "" },
    rounding: Rounding = Rounding.ROUND_HALF_UP,
  ): string {
    const price = +this.toFixed(decimalPlaces, format, rounding)

    return price.toLocaleString("en", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      style: "decimal",
      notation: price > 1e6 ? "compact" : "standard",
    })
  }

  /**
   * Returns a JSON representation of the `Fraction` with numerator and denominator as strings.
   * @returns {object} - An object with 'n' (numerator) and 'd' (denominator) properties.
   */
  toJSON() {
    return {
      n: this.numerator.toString(),
      d: this.denominator.toString(),
    }
  }

  /**
   * Helper method for converting the current `Fraction` to its fractional form.
   * @returns {Fraction} - A new `Fraction` instance representing the current `Fraction`.
   */
  get asFraction(): Fraction {
    return new Fraction(this.numerator, this.denominator)
  }
}

export default Fraction
